import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import AdminLayout from './AdminLayout';
import {
  BookOpen, MessageSquare, AlertCircle, CheckCircle2,
  Database, ArrowRight, Loader2, RefreshCw, Inbox
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { authHeaders } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [recentEnquiries, setRecentEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [seedMsg, setSeedMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, enqRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: authHeaders }),
        axios.get(`${API}/admin/enquiries`, { headers: authHeaders }).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setRecentEnquiries(enqRes.data.slice(0, 5));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const handleSeed = async () => {
    if (!window.confirm('This will replace all existing courses with the starter catalogue. Continue?')) return;
    setSeeding(true);
    setSeedMsg('');
    try {
      const res = await axios.post(`${API}/admin/courses/seed`, {}, { headers: authHeaders });
      setSeedMsg(`✓ ${res.data.seeded} courses imported successfully!`);
      load();
    } catch (e) {
      setSeedMsg(e?.response?.data?.detail || 'Seed failed. Is Supabase configured?');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-5xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-500 text-sm mt-0.5">Overview of your Atlas Academy site</p>
          </div>
          <button onClick={load} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {/* Supabase status banner */}
        {stats && !stats.supabase_configured && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <AlertCircle size={20} className="text-amber-500 shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-amber-800 text-sm">Supabase not yet configured</p>
              <p className="text-amber-700 text-xs mt-0.5">
                Add <code className="bg-amber-100 px-1 rounded">SUPABASE_URL</code> and{' '}
                <code className="bg-amber-100 px-1 rounded">SUPABASE_SERVICE_KEY</code> to{' '}
                <code className="bg-amber-100 px-1 rounded">backend/.env</code>, then restart the server.
                Courses are currently served from the built-in mock catalogue.
              </p>
            </div>
            <a href="https://supabase.com/dashboard" target="_blank" rel="noopener noreferrer"
              className="text-xs bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg font-medium whitespace-nowrap transition-colors">
              Open Supabase
            </a>
          </div>
        )}

        {stats && stats.supabase_configured && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
            <CheckCircle2 size={18} className="text-green-600" />
            <p className="text-green-800 text-sm font-medium">Supabase connected and active</p>
          </div>
        )}

        {/* Stats cards */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
              icon={BookOpen}
              label="Total Courses"
              value={stats?.courses ?? 0}
              color="blue"
              link="/admin/courses"
            />
            <StatCard
              icon={MessageSquare}
              label="Total Enquiries"
              value={stats?.enquiries ?? 0}
              color="indigo"
              link="/admin/enquiries"
            />
            <StatCard
              icon={Inbox}
              label="New Enquiries"
              value={stats?.new_enquiries ?? 0}
              color="green"
              link="/admin/enquiries"
              highlight={stats?.new_enquiries > 0}
            />
          </div>
        )}

        {/* Quick actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Database size={18} className="text-blue-600" />
              <h3 className="font-semibold text-gray-900 text-sm">Seed Course Catalogue</h3>
            </div>
            <p className="text-gray-500 text-xs leading-relaxed mb-4">
              Import the built-in starter courses into Supabase. Requires Supabase to be configured.
              This will overwrite any existing courses.
            </p>
            {seedMsg && (
              <p className={`text-xs mb-3 font-medium ${
                seedMsg.startsWith('✓') ? 'text-green-600' : 'text-red-500'
              }`}>{seedMsg}</p>
            )}
            <button
              onClick={handleSeed}
              disabled={seeding || (stats && !stats.supabase_configured)}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-xs font-semibold transition-colors"
            >
              {seeding ? <><Loader2 size={13} className="animate-spin" /> Seeding…</> : <><Database size={13} /> Import Starter Courses</>}
            </button>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen size={18} className="text-blue-600" />
              <h3 className="font-semibold text-gray-900 text-sm">Manage Courses</h3>
            </div>
            <p className="text-gray-500 text-xs leading-relaxed mb-4">
              Add, edit, or remove courses from your catalogue. Changes are reflected on the public site immediately.
            </p>
            <Link to="/admin/courses"
              className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-xs font-semibold transition-colors">
              Go to Courses <ArrowRight size={13} />
            </Link>
          </div>
        </div>

        {/* Recent enquiries */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 text-sm">Recent Enquiries</h3>
            <Link to="/admin/enquiries" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
              View all <ArrowRight size={12} />
            </Link>
          </div>
          {recentEnquiries.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <Inbox size={28} className="text-gray-200 mx-auto mb-2" />
              <p className="text-gray-400 text-sm">{stats?.supabase_configured ? 'No enquiries yet' : 'Supabase required to store enquiries'}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {recentEnquiries.map(e => (
                <div key={e.id} className="px-5 py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {e.first_name} {e.last_name}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{e.email} {e.course_title ? `· ${e.course_title}` : ''}</p>
                  </div>
                  <StatusBadge status={e.status} />
                  <p className="text-xs text-gray-400 whitespace-nowrap">{new Date(e.created_at).toLocaleDateString('en-GB')}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function StatCard({ icon: Icon, label, value, color, link, highlight }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    green: 'bg-green-50 text-green-600',
  };
  return (
    <Link to={link} className={`bg-white rounded-xl border p-5 hover:shadow-md transition-shadow ${
      highlight ? 'border-green-300' : 'border-gray-200'
    }`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${colors[color]}`}>
        <Icon size={20} />
      </div>
      <p className="text-2xl font-extrabold text-gray-900">{value}</p>
      <p className="text-gray-500 text-sm mt-0.5">{label}</p>
    </Link>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    new: 'bg-blue-100 text-blue-700',
    read: 'bg-gray-100 text-gray-600',
    responded: 'bg-green-100 text-green-700',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${cfg[status] || cfg.new}`}>
      {status}
    </span>
  );
}
